import { Navigate } from 'react-router-dom';
import { useAuth } from '@core/auth/hooks/useAuth';
import { getLastModuleId, MODULE_CHOOSER_PATH } from './moduleLanding';
import { useAccessibleModules } from './useAccessibleModules';

/**
 * SmartRedirect Component
 *
 * Decides where the user lands after login (and on "/" or "/dashboard").
 * Administrative modules (e.g. Users) do not count as work modules.
 *
 * Logic:
 * 1. Last used work module, if the user still has access to it
 * 2. The only work module, if there is exactly one
 * 3. Module chooser page, if there are several work modules
 * 4. An administrative module, if that is all the user has
 * 5. Unauthorized page otherwise
 *
 * @example
 * // In AppRouter:
 * <Route index element={<SmartRedirect />} />
 */
export const SmartRedirect = () => {
  const { user } = useAuth();
  const { accessibleModules } = useAccessibleModules();

  const workModules = accessibleModules.filter((module) => !module.isAdministrative);
  const lastModuleId = user?.id ? getLastModuleId(user.id) : null;
  const lastModule = workModules.find((module) => module.id === lastModuleId);

  let targetPath: string;
  if (lastModule) {
    targetPath = lastModule.basePath;
  } else if (workModules.length === 1) {
    targetPath = workModules[0].basePath;
  } else if (workModules.length > 1) {
    targetPath = MODULE_CHOOSER_PATH;
  } else if (accessibleModules.length > 0) {
    targetPath = accessibleModules[0].basePath;
  } else {
    targetPath = '/unauthorized';
  }

  return <Navigate to={targetPath} replace />;
};

export default SmartRedirect;
